import React from 'react';
import { Box } from '@mui/material';

import { ManualLoadSettings } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';

import { editManualLoadInComponentDesign } from '../../../../store/actions/editAdditionalLoadingActions';
import { calculateActiveComponentMembersMinMax } from '../../../../store/selectors/boundsSelectors';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getLoadCasesForActiveComponentDesignSorted } from '../../../../store/selectors/getLoadCasesForActiveComponentDesignSortedSelector';
import { inViewOnlyMode } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { AddManualLoadToSelect } from '../insertions/AddManualLoadToSelect';
import { manualLoadCoordinateIsOnComponent } from '../insertions/manualLoadValidation';
import { ManualLoadFields } from '../manual-loads/ManualLoadFields';

interface Props {
  manualLoadSettings: ManualLoadSettings;
}

export function ManualLoadSettingsEdit(props: Props) {
  const { manualLoadSettings } = props;
  const { load: loadOrLoadDefinition } = manualLoadSettings;
  const dispatch = useAppDispatch();
  const componentDesign = useAppSelector(getActiveComponentDesign);
  const loadCases = useAppSelector(getLoadCasesForActiveComponentDesignSorted);
  const isViewOnly = useAppSelector(inViewOnlyMode);
  const componentBounds = useAppSelector(calculateActiveComponentMembersMinMax);

  if (!componentDesign || !componentBounds)
    return <InlineNotification type="error">No active component</InlineNotification>;

  return (
    <PropertiesPanel title="Manual Load Properties">
      <ManualLoadFields
        loadOrLoadDefinition={manualLoadSettings.load}
        component={componentDesign.component}
        onChangeLoadOrLoadDefinition={(updatedLoadOrLoadDefinition) =>
          void dispatch(
            editManualLoadInComponentDesign(componentDesign, {
              ...manualLoadSettings,
              load: updatedLoadOrLoadDefinition,
            }),
          )
        }
        coordinateIsOnComponent={manualLoadCoordinateIsOnComponent(componentBounds)}
        disabled={isViewOnly}
      />
      <Box sx={{ display: 'flex' }}>
        <AddManualLoadToSelect
          sx={{ m: 1 }}
          loadCases={loadCases}
          componentDesign={componentDesign}
          loadType={
            loadOrLoadDefinition.loadInsertionMethod === 'Points'
              ? loadOrLoadDefinition.load.loadType
              : loadOrLoadDefinition.loadDefinition.loadType
          }
          loadCaseIdentification={props.manualLoadSettings.loadCaseIdentification}
          specifiedLoadCaseName={props.manualLoadSettings.specifiedLoadCaseName}
          onSelectLoadDestination={(loadCaseIdentification, specifiedLoadCaseName) => {
            const updatedManualLoadSettings: ManualLoadSettings = {
              ...props.manualLoadSettings,
              loadCaseIdentification,
              specifiedLoadCaseName,
            };
            dispatch(editManualLoadInComponentDesign(componentDesign, updatedManualLoadSettings));
          }}
          disabled={isViewOnly}
        />
      </Box>
    </PropertiesPanel>
  );
}
